<template>
  <div class="reports-eco-events cols">
    <div class="info-strip rows mobile-cols justify-space-between align-items-center">
      <button class="transparent font-size-20" @click="back">
        <i class="bi bi-arrow-left-circle"></i>
      </button>

      <div class="date-time-interval"> {{ vehicleName }} </div>

      <div class="date-time-interval nowrap">
        <CurrentTime :date-time-moment="tripRecordStartDate"/>
        <span class="desktop">&nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;</span><br class="mobile">
        <CurrentTime :date-time-moment="tripRecordEndDate"/>
      </div>

      <div class="eco-score rows align-items-center">
        <img
            src="@/assets/images/reports/info-strip-hard-acceleration.png"
            :alt="$t('reports.trips.eco-events.hard-acceleration')"
        />
        <span class="description">
            {{ $t('reports.trips.eco-events.hard-acceleration') }}
          </span>
        <span class="value">
            {{ hardAccelerationCount }}
          </span>
      </div>

      <div class="eco-score rows align-items-center">
        <img
            src="@/assets/images/reports/info-strip-hard-braking.png"
            :alt="$t('reports.trips.eco-events.hard-braking')"
        />
        <span class="description">
            {{ $t('reports.trips.eco-events.hard-braking') }}
          </span>
        <span class="value">
            {{ hardBrakingCount }}
          </span>
      </div>

      <div class="eco-score rows align-items-center">
        <img
            src="@/assets/images/reports/info-strip-parking.png"
            :alt="$t('reports.trips.eco-events.parking')"
        />
        <span class="description">
            {{ $t('reports.trips.eco-events.parking') }}
          </span>
        <span class="value">
            {{ parkingCount }}
          </span>
      </div>
    </div>
    <div class="rows checkbox-container">
      <div class="pr-10">
        <button type="button" @click="addGeoFenceAtMapCenter">
          {{ $t('geo-fences.add') }}
        </button>
      </div>
      <div class="rows align-items-center pr-10">
        <input
            id="showAlarms"
            type="checkbox"
            v-model="showAlarms"
            class="big-check-box"
        />
        <label for="showAlarms">
          {{ $t('reports.trips.eco-events.show-alarms') }}
        </label>
      </div>
      <div class="rows align-items-center pr-10">
        <input
            id="showStops"
            type="checkbox"
            v-model="showStops"
            class="big-check-box"
        />
        <label for="showStops">
          {{ $t('reports.trips.eco-events.show-stops') }}
        </label>
      </div>
      <div class="rows align-items-center pr-10">
        <input
            id="showStay"
            type="checkbox"
            v-model="showStay"
            class="big-check-box"
        />
        <label for="showStay">
          {{ $t('reports.trips.eco-events.show-stay') }}
        </label>
      </div>
      <div class="rows align-items-center pr-10">
        <input
            id="showVehicle"
            type="checkbox"
            v-model="showVehicle"
            class="big-check-box"
        />
        <label for="showVehicle">
          {{ $t('reports.trips.vehicle') }}
        </label>
      </div>
      <div class="rows align-items-center">
        <input
            id="showGeoFences"
            type="checkbox"
            v-model="showGeoFences"
            class="big-check-box"
        />
        <label for="showGeoFences">
          {{ $t('reports.trips.eco-events.show-zones') }}
        </label>
      </div>
      <div class="stop-colors-info-link-container">
        <button type="button" class="transparent stop-colors-info-link" @click="openStopColorsInfoPopup">
          {{ $t('reports.trips.eco-events.stop-colors.info-link') }}
        </button>
      </div>
    </div>
    <PositionsSlider
        v-if="showSlider"
        v-model="selectedPosition"
        :slider-positions="sliderPositions"
        @change="onPositionSliderChange"
    />
    <div class="map-pad">
      <Map ref="googleMapRef"
           class="map"
           mapTypeId="terrain"
           :zoom="15"
           :enable-measure-tool="true"
           scrollwheel
           @on-map-ready="mapReady"
           @idle="onMapViewportChanged">
        <CircleGeoFence v-for="geoFenceData in visibleUserGeoFences" v-bind:key="geoFenceData.id"
                        ref="geoFenceAreasRef"
                        :geoFenceData="geoFenceData"
                        :allow-edit="true"
                        :hover-enabled="false"
                        @click="geoFenceAreaClick" />
        <CircleGeoFence v-if="newGeoFenceData"
                        ref="newGeofenceAreaRef"
                        :geoFenceData="newGeoFenceData"
                        :allow-edit="true"
                        :hover-enabled="false"
                        @click="geoFenceAreaClick"
                        @created="onNewGeoFenceCreated"
                        @cancel_edit_mode="cancelGeoFenceCreate" />
        <MapRoute ref="mapRoutes" v-for="(data, index) in tripsRecordPositionsWithStops" v-bind:key="index"
                  :positions="data.positions" :stay="data.stay" :stops="data.stops"
                  :trip="data.trip" :index="data.index" :show-alarms="showAlarms"
                  :show-stays="showStay" :show-stops="showStops"
                  @selected="onTripSelected" @mouse-move="selectedRouteMouseMove" />
        <template v-if="showVehicle">
          <VehicleMarker v-for="selectedVehiclePosition in selectedVehiclePositions"
                         v-bind:key="selectedVehiclePosition.deviceId"
                         ref="vehicleMarkerRef"
                         :positionData="selectedVehiclePosition"
                         :basic-mode="true"/>
        </template>
      </Map>
    </div>
  </div>
</template>

<script>
import {createApp} from "vue";
import {mapGetters, mapState} from 'vuex';
import {dataManipulator, entityDataManager, GoogleMapsManager, WorkflowManager} from '@/assets/js/data-manipulation';
import moment from 'moment';
import i18n from "@/assets/js/vue/i18n";
import CurrentTime from '@/components/pages/partials/CurrentTime';
import Map from "@/components/pages/partials/maps/Map.vue";
import CircleGeoFence from "@/components/pages/partials/maps/CircleGeoFence.vue";
import MapRoute from "@/components/pages/partials/maps/MapRoute.vue";
import VehicleMarker from "@/components/pages/partials/maps/VehicleMarker.vue";
import PositionsSlider from "@/components/pages/partials/PositionsSlider.vue";
import StopColorsInfoPopup from "@/components/pages/partials/StopColorsInfoPopup.vue";
import {GeoFenceType} from "@/assets/js/enums/GeoFenceType.ts";
import {GeoFenceColor} from "@/assets/js/enums/GeoFenceColor.ts";
import {mutations} from "@/assets/js/vue/vuex/actions";

export default {
  name: 'EcoEvents',
  components: {
    VehicleMarker,
    MapRoute,
    CircleGeoFence,
    Map,
    CurrentTime,
    PositionsSlider,
  },
  data() {
    return {
      specificPositionTime: null,
      showAlarms: Boolean(+process.env.VUE_APP_TRIPS_SHOW_ALARMS_DEFAULT),
      showStops: Boolean(+process.env.VUE_APP_TRIPS_SHOW_STOPS_DEFAULT),
      showStay: Boolean(+process.env.VUE_APP_TRIPS_SHOW_STAY_DEFAULT),
      showVehicle: true,
      showGeoFences: true,
      selectedTrip: null,
      selectedPosition: null,
      selectedVehiclePositions: [],
      newGeoFenceData: null,
      mapBounds: null,
    };
  },
  computed: {
    ...mapState({
      storedTripsRecordPositions: state => state.tripsRecordPositions,
      storedTripsData: state => state.tripsData,
      tripsMapData: state => state.tripsMapData,
      userGeoFences: state => state.userGeoFences,
      allVehicles: state => state.vehicles,
      positions: state => state.positions,
      tripsFilterDateFrom: state => state.tripsFilterDateFrom,
      tripsFilterDateTo: state => state.tripsFilterDateTo,
      tripsFilterDevice: state => state.tripsFilterDevice,
    }),
    ...mapGetters(['isAdmin']),
    usesTripsMapCache() {
      return this.$route.query.tripsMap === '1';
    },
    tripsData() {
      return this.usesTripsMapCache ? this.tripsMapData?.data ?? [] : this.storedTripsData;
    },
    tripsRecordPositions() {
      return this.usesTripsMapCache
          ? dataManipulator.getTripsRecordPositionsFromMapData(this.tripsData)
          : this.storedTripsRecordPositions;
    },
    deviceIds() {
      return [...new Set(this.tripsRecords.map(record => record.deviceId).filter(deviceId => deviceId != null))];
    },
    vehicles() {
      return this.allVehicles.filter(vehicle => this.deviceIds.includes(vehicle.deviceId));
    },
    vehicleName() {
      const selectedTripDeviceId = this.selectedTrip?.trip?.deviceId ?? this.tripsRecords[0]?.deviceId ?? null;
      return entityDataManager.getVehicleByDeviceId(this.vehicles, selectedTripDeviceId)?.nickname ?? '';
    },
    tripsRecords() {
      return this.tripsData?.trips ?? [];
    },
    tripsRecordPositionsFlat() {
      return this.tripsRecordPositions.reduce((acc, tripRecordPosition) => [...acc, ...tripRecordPosition], []);
    },
    tripRecordPositionsFixTimes() {
      return this.tripsRecordPositionsFlat.map(position => position['fixTime']);
    },
    tripRecordPositionsMinMaxTimes() {
      return [this.tripRecordPositionsFixTimes[0], this.tripRecordPositionsFixTimes[this.tripRecordPositionsFixTimes.length - 1]];
    },
    tripRecordStartDate() {
      if (this.tripRecordPositionsMinMaxTimes[0] === undefined) {
        return moment();
      }

      return moment(this.tripRecordPositionsMinMaxTimes[0]);
    },
    tripRecordEndDate() {
      if (this.tripRecordPositionsMinMaxTimes[1] === undefined) {
        return moment();
      }

      return moment(this.tripRecordPositionsMinMaxTimes[1]);
    },
    hardAccelerationCount() {
      return this.getAlarmTypePositions('hardAcceleration')?.length ?? 0;
    },
    hardBrakingCount() {
      return this.getAlarmTypePositions('hardBraking')?.length ?? 0;
    },
    parkingCount() {
      return this.tripsRecordPositionsWithStops
          ?.reduce((sum, item) => sum + (item.stops?.length ?? 0), 0) ?? 0;
    },
    showSlider() {
      return this.sliderPositions?.length > 1;
    },
    sliderPositions() {
      return this.tripsRecordPositionsWithStops.reduce((acc, tripRecordPosition) => {
        const mappedPositions = tripRecordPosition.positions.map(position => {
          const timestamp = dataManipulator.iso8601DateStringToLocalMoment(position.fixTime);

          return {
            value: position.id,
            label: timestamp !== null ? timestamp.format(this.$t('settings.generic.date-time-format-moment')) : '',
            info: {
              tripIndex: tripRecordPosition.index,
            }
          }
        });

        return [...acc, ...mappedPositions];
      }, []);
    },
    tripsRecordPositionsWithStops() {
      return entityDataManager.getTripsRecordPositionsWithStops(
          this.tripsRecords,
          this.tripsRecordPositions,
          this.tripsRecordPositionsFlat,
          this.tripsData?.mapData?.routes,
      );
    },
    visibleUserGeoFences() {
      if (!this.showGeoFences) {
        return [];
      }

      if (!this.mapBounds) {
        return this.userGeoFences;
      }

      return this.userGeoFences.filter(geoFence => this.isGeoFenceVisibleInBounds(geoFence, this.mapBounds));
    }
  },
  watch: {
    deviceIds: {
      handler() {
        this.updateSelectedVehiclePositions();
      },
      immediate: true,
    },
    positions() {
      this.updateSelectedVehiclePositions();
    },
  },
  async mounted() {
    this.specificPositionTime = this.$route.query.specificTime;

    if (this.$route.query.search === '1') {
      this.$store.commit(mutations.SET_TRIPS_FILTER_DATE_FROM, {date: this.$route.query.dateFrom});
      this.$store.commit(mutations.SET_TRIPS_FILTER_DATE_TO, {date: this.$route.query.dateTo});
      this.$store.commit(mutations.SET_TRIPS_FILTER_DEVICE_ID, {deviceId: +this.$route.query.deviceId});
      this.$store.commit(mutations.SET_TRIPS_FILTER_DRIVER, null);
      this.$store.commit(mutations.SET_TRIPS_FILTER_GROUP, null);
      this.$store.commit(mutations.SET_TRIPS_FILTER_INCLUDE_ADDRESSES, true);
      await this.loadData();
      if (this.$refs.googleMapRef?.loaded) {
        this.mapReady();
      }
    }
    else if (!this.specificPositionTime && this.tripsRecordPositions.length === 0) {
      this.back();
      return;
    }

    this.selectedPosition = this.sliderPositions[0]?.value ?? null;

  },
  methods: {
    back() {
      this.$router.push({path: '/reports/trips'});
    },
    mapReady() {
      this.fitMapToRoute();
      this.onMapViewportChanged();
    },
    onMapViewportChanged() {
      const bounds = this.$refs.googleMapRef?.map?.getBounds?.();
      if (!bounds) {
        return;
      }

      const northEast = bounds.getNorthEast();
      const southWest = bounds.getSouthWest();
      if (!northEast || !southWest) {
        return;
      }

      this.mapBounds = {
        north: northEast.lat(),
        east: northEast.lng(),
        south: southWest.lat(),
        west: southWest.lng(),
      };
    },
    isGeoFenceVisibleInBounds(geoFence, bounds) {
      const center = geoFence?.area?.coordinates;
      const radiusMeters = geoFence?.area?.radius;
      if (!center || !Number.isFinite(radiusMeters)) {
        return false;
      }

      const lat = center.lat;
      const lng = center.lng;
      const latRadius = radiusMeters / 111320;
      const cosLat = Math.cos((lat * Math.PI) / 180);
      const lngRadius = radiusMeters / (111320 * Math.max(0.000001, Math.abs(cosLat)));

      return !(lat + latRadius < bounds.south ||
          lat - latRadius > bounds.north ||
          lng + lngRadius < bounds.west ||
          lng - lngRadius > bounds.east);
    },
    async loadData() {
      await WorkflowManager.loadTripsMapDataFromCurrentFilters(this.specificPositionTime);
    },
    updateSelectedVehiclePositions() {
      const deviceIds = new Set(this.deviceIds);
      this.selectedVehiclePositions = this.positions?.filter(position => deviceIds.has(position.deviceId)) ?? [];
    },
    onTripSelected(trip) {
      this.selectTrip(trip.index);
      this.selectedRouteMouseMove(trip);
    },
    selectTrip(selectedIndex) {
      if (this.selectedTrip?.index === selectedIndex) {
        return;
      }

      this.$refs.mapRoutes.forEach(route => {
        if (route.index === selectedIndex) {
          this.selectedTrip = route;
          return;
        }

        route.deselectPosition();
      });
    },
    selectedRouteMouseMove(route) {
      this.selectedPosition = route.selectedPosition.id;
    },
    onPositionSliderChange(value, sliderPosition) {
      if (!sliderPosition) {
        return;
      }

      const {info} = sliderPosition;
      this.selectTrip(info.tripIndex);
      this.selectedTrip.selectPositionById(value);
      this.selectedRouteMouseMove(this.selectedTrip);
      setTimeout(() => this.selectedTrip.initializeInfoWindow(), 10);
    },
    geoFenceAreaClick(area) {
      area?.showInfoWindow();
    },
    cancelGeoFenceCreate() {
      this.newGeoFenceData = null;
    },
    addGeoFenceAtMapCenter() {
      const mapCenter = this.$refs.googleMapRef?.map?.center;
      if (!mapCenter) {
        return;
      }

      this.newGeoFenceData = {
        type: GeoFenceType.Circle,
        id: 0,
        name: '',
        description: '',
        area: {
          radius: parseInt(process.env.VUE_APP_NEW_GEO_FENCE_CIRCLE_RADIUS, 10),
          coordinates: GoogleMapsManager.getCoordinatesFromGoogleData(mapCenter),
        },
        attributes: {
          color: GeoFenceColor.Green,
        },
      };
      setTimeout(() => {
        this.$refs.newGeofenceAreaRef?.showInfoWindow();
        this.$refs.newGeofenceAreaRef?.editGeoFence();
      });
    },
    onNewGeoFenceCreated() {
      this.newGeoFenceData = null;
    },
    getGoogleMapPositions(positions) {
      return positions.map(position => entityDataManager.getGoogleGeoByPosition(position));
    },
    fitMapToRoute() {
      const allGoogleMapPositions = this.tripsRecordPositions.reduce(
          (acc, tripRecordPositions) => [...acc, ...this.getGoogleMapPositions(tripRecordPositions)],
          [],
      );

      const newBoundary = GoogleMapsManager.getMapBoundsByPositions(allGoogleMapPositions);

      this.$refs.googleMapRef?.map?.fitBounds(newBoundary);
    },
    getAlarmTypePositions(type) {
      return this.tripsRecordPositionsFlat.filter((position) => position['alarm'] === type);
    },
    async openStopColorsInfoPopup() {
      const wrapper = document.createElement('div');
      let componentApp = null;

      componentApp = createApp(StopColorsInfoPopup, {
        onClose: async () => {
          await this.$swal.close();
        }
      });
      componentApp.use(i18n);
      componentApp.mount(wrapper);

      await this.$swal.fire({
        title: this.$t('reports.trips.eco-events.stop-colors.info-link'),
        html: wrapper,
        width: 'fit-content',
        showConfirmButton: false,
        showDenyButton: false,
        showCancelButton: false,
        didClose: () => componentApp?.unmount(),
      });
    },
    
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables.scss';

.reports-eco-events {
  .info-strip {
    padding-top: 20px;
    padding-right: 10px;

    .date-time-interval {
      margin-left: 10px;

      font-weight: 400;
      font-size: 1.2rem;
      line-height: 27px;
    }

    .eco-score {
      height: 25px;
      padding: 10px 14px;
      margin-left: 10px;

      background: $tracking-blue;
      border-radius: 8px;

      font-size: 0.8rem;
      line-height: 21px;

      img {
        height: 100%;
      }

      .description {
        margin: 0 10px;
        font-weight: 400;
      }

      .value {
        font-weight: 700;
      }
    }
  }

  .checkbox-container {
    align-items: center;

    .stop-colors-info-link-container {
      margin-left: auto;
      padding-right: 10px;
    }

    .stop-colors-info-link {
      color: $tracking-accent;
      font-size: 0.9rem;
      line-height: 21px;
      text-decoration: underline;
    }
  }

  .map-pad {
    width: calc(100% - 20px);
    height: 700px;
    padding: 10px;

    .map {
      width: 100%;
      height: 100%;
      border-radius: 15px;
    }
  }
}

// Mobile:
@media (max-width: $breaking-point) {
  .reports-eco-events {
    .info-strip {
      .date-time-interval {
        margin-left: 0 !important;
      }

      .eco-score {
        margin-top: 10px;
        margin-left: 0 !important;
      }
    }
  }
}
</style>
